// Vista de autenticación (Req. 17.2, 19.11): login, primer cambio de contraseña
// (usuario invitado) y recuperación de contraseña por email. No revela si un
// correo existe (Req. 19.12): el flujo de "olvidé mi contraseña" pide el código
// sin confirmar existencia.
//
// Diseño: sistema "Audiophile Precision" (basado en el prototipo screen-02,
// frontend/design/extracted/screen-02). Sin barra de título superior: la vista
// arranca directo con el bloque hero (logo saands grande + marca) y el
// formulario de login. La recuperación de contraseña se accede desde el enlace
// "¿Olvidaste tu contraseña?"; el modo reset (código OTP + nueva clave) solo se
// muestra DESPUÉS de solicitar el código con el correo. Verificación de
// políticas Cognito en vivo; CTAs cian (vacuum-glow); footer fijo. La lógica de
// Cognito se mantiene intacta.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import {
  completeNewPassword,
  confirmForgotPassword,
  forgotPassword,
  login,
  type AuthChallenge,
} from '../auth/cognito.js';
import { hasCognitoConfig } from '../config.js';
import { AlertMessage, Icon } from '../ui/components.js';
import { useAuthMessages } from '../auth/useAuthMessages.js';
import { SocialLoginButtons, SocialDivider } from '../auth/SocialLoginButtons.js';

type Mode = 'login' | 'new_password' | 'forgot' | 'reset';

export function LoginView() {
  const { t } = useTranslation();
  const { passwordPolicy, loginError } = useAuthMessages();
  const { setSession } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function doLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const outcome = await login(email.trim(), password);
      if ('challenge' in outcome) {
        setChallenge(outcome.challenge);
        setMode('new_password');
      } else {
        setSession(outcome.tokens, email.trim());
      }
    } catch (err) {
      setError(loginError(err));
    } finally {
      setBusy(false);
    }
  }

  async function doNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const tokens = await completeNewPassword(challenge, newPassword);
      setSession(tokens, challenge.username);
    } catch (err) {
      setError(loginError(err));
    } finally {
      setBusy(false);
    }
  }

  async function doForgot(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      // No revela si el correo existe (Req. 19.12): mensaje neutro.
      setInfo(t('login.forgotInfo'));
      setMode('reset');
    } catch (err) {
      setError(loginError(err));
    } finally {
      setBusy(false);
    }
  }

  async function doReset(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmForgotPassword(email.trim(), code.trim(), newPassword);
      setInfo(t('login.resetDone'));
      setMode('login');
      setPassword('');
    } catch (err) {
      setError(loginError(err));
    } finally {
      setBusy(false);
    }
  }

  const policy = passwordPolicy(newPassword, 'login');
  const policyMet = policy.filter((r) => r.ok).length;

  return (
    <div className="min-h-screen bg-surface-base text-on-surface flex flex-col font-body-md text-body-md">
      <main className="flex-1 w-full max-w-md mx-auto px-margin-mobile pt-safe pb-20 flex flex-col">
        <div className="flex flex-col w-full pb-space-xl">
          {/* --- Bloque hero: logo saands (grande) + marca --- */}
          <div className="mt-space-md mb-space-lg text-center flex flex-col items-center">
            <img
              src="/logo-saands.png"
              alt="saands"
              className="h-44 w-auto object-contain drop-shadow-[0_0_32px_rgba(34,211,238,0.25)]"
            />
          </div>

          {/* --- Avisos (config / error / info) --- */}
          {!hasCognitoConfig() && (
            <div className="mb-space-md p-space-sm rounded-lg bg-goldmine-vg/15 border border-goldmine-vg/40 text-goldmine-vg font-body-sm text-body-sm flex items-start gap-space-xs">
              <Icon name="warning" className="text-[18px] shrink-0" />
              {t('login.cognitoNotConfigured')}
            </div>
          )}
          <AlertMessage message={error} className="mb-space-md" />
          {info && (
            <div className="mb-space-md p-space-sm rounded-lg bg-primary-container/15 border border-primary-container/40 text-primary-container font-body-sm text-body-sm flex items-start gap-space-xs">
              <Icon name="info" className="text-[18px] shrink-0" />
              {info}
            </div>
          )}

          {/* --- LOGIN SOCIAL (Google / Apple) + separador. Solo si el login
                 federado está configurado; convive con email+contraseña. --- */}
          {mode === 'login' && (
            <>
              <SocialLoginButtons returnTo="/" />
              <SocialDivider />
            </>
          )}

          {/* --- LOGIN --- */}
          {mode === 'login' && (
            <form onSubmit={doLogin} className="flex flex-col gap-space-md">
              <div className="flex flex-col gap-space-xs">
                <label htmlFor="email" className="field-label flex items-center gap-1">
                  <Icon name="contact_mail" className="text-[14px] text-tertiary" />
                  {t('login.emailLabel')}
                </label>
                <div className="field-slot">
                  <Icon name="person" className="text-on-surface-variant text-[18px] mr-space-xs" />
                  <input
                    id="email"
                    className="field-input"
                    type="email"
                    value={email}
                    autoComplete="username"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col gap-space-xs">
                <div className="flex items-center justify-between">
                  <label htmlFor="pw" className="field-label flex items-center gap-1">
                    <Icon name="password" className="text-[14px] text-primary-container" />
                    {t('login.passwordLabel')}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError(null);
                    }}
                    className="font-label-caps text-label-caps text-tertiary hover:underline"
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>
                <div className="field-slot pr-space-xs">
                  <Icon name="key" className="text-on-surface-variant text-[18px] mr-space-xs" />
                  <input
                    id="pw"
                    className="field-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    autoComplete="current-password"
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-on-surface-variant hover:text-primary-container transition-colors"
                  >
                    <Icon name={showPassword ? 'visibility_off' : 'visibility'} className="text-[18px]" />
                  </button>
                </div>
              </div>
              <button className="btn-cyan w-full" type="submit" disabled={busy}>
                <Icon name="play_arrow" className="text-[20px]" />
                {busy ? t('login.submitBusy') : t('login.submit')}
              </button>
            </form>
          )}

          {/* --- PRIMER INGRESO: NUEVA CONTRASEÑA --- */}
          {mode === 'new_password' && (
            <form onSubmit={doNewPassword} className="flex flex-col gap-space-md">
              <div className="p-space-sm bg-surface-deck border border-border-subtle rounded-lg flex items-center gap-space-sm">
                <Icon name="key_vertical" className="text-tertiary text-[24px] shrink-0" />
                <div className="flex flex-col">
                  <span className="section-label text-on-surface font-semibold">
                    {t('login.newPasswordTitle')}
                  </span>
                  <span className="font-badge-micro text-badge-micro text-on-surface-variant">
                    {t('login.newPasswordSubtitle')}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-space-xs">
                <label htmlFor="np" className="field-label">
                  {t('login.newPasswordLabel')}
                </label>
                <div className="field-slot">
                  <Icon name="password" className="text-on-surface-variant text-[18px] mr-space-xs" />
                  <input
                    id="np"
                    className="field-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              <PolicyChecklist policy={policy} met={policyMet} title={t('auth.password.title')} metLabel={t('auth.password.metOf', { met: policyMet, total: policy.length })} />
              <button
                className="btn-cyan w-full"
                type="submit"
                disabled={busy || policyMet < policy.length}
              >
                <Icon name="check_circle" className="text-[20px]" />
                {busy ? t('login.saving') : t('login.saveAndEnter')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
                className="w-full min-h-[44px] py-2.5 px-space-md rounded-lg bg-surface-deck border border-border-subtle text-on-surface-variant font-body-sm text-body-sm hover:text-on-surface transition-colors flex items-center justify-center gap-1"
              >
                <Icon name="close" className="text-[16px]" />
                {t('login.cancel')}
              </button>
            </form>
          )}

          {/* --- OLVIDÉ: SOLICITAR CÓDIGO --- */}
          {mode === 'forgot' && (
            <form onSubmit={doForgot} className="flex flex-col gap-space-md">
              <div className="flex flex-col gap-space-xs">
                <label htmlFor="fe" className="field-label flex items-center gap-1">
                  <Icon name="mail" className="text-[14px] text-tertiary" />
                  {t('login.registeredEmailLabel')}
                </label>
                <div className="field-slot">
                  <Icon name="alternate_email" className="text-on-surface-variant text-[18px] mr-space-xs" />
                  <input
                    id="fe"
                    className="field-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button className="btn-cyan w-full" type="submit" disabled={busy}>
                <Icon name="send" className="text-[20px]" />
                {busy ? t('login.sending') : t('login.sendCode')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
                className="w-full min-h-[44px] py-2.5 px-space-md rounded-lg bg-surface-deck border border-border-subtle text-on-surface-variant font-body-sm text-body-sm hover:text-on-surface transition-colors flex items-center justify-center gap-1"
              >
                <Icon name="arrow_back" className="text-[16px]" />
                {t('login.backToLogin')}
              </button>
            </form>
          )}

          {/* --- RESET: CÓDIGO OTP + NUEVA CONTRASEÑA --- */}
          {mode === 'reset' && (
            <form onSubmit={doReset} className="flex flex-col gap-space-md">
              <div className="p-space-sm bg-surface-deck border border-border-subtle rounded-lg flex items-center gap-space-sm">
                <Icon name="key_vertical" className="text-tertiary text-[24px] shrink-0" />
                <div className="flex flex-col">
                  <span className="section-label text-on-surface font-semibold">
                    {t('login.newPasswordTitle')}
                  </span>
                  <span className="font-badge-micro text-badge-micro text-on-surface-variant">
                    {t('login.newPasswordSubtitle')}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-space-xs">
                <label htmlFor="code" className="field-label">
                  {t('login.otpLabel')}
                </label>
                <input
                  id="code"
                  className="w-full bg-surface-deck border border-border-subtle rounded-lg py-3 px-space-md text-on-surface font-code-catalog text-code-catalog tracking-widest text-center focus:outline-none focus:border-border-focus focus:bg-surface-rack"
                  value={code}
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-space-xs">
                <label htmlFor="rp" className="field-label">
                  {t('login.newPasswordLabel')}
                </label>
                <input
                  id="rp"
                  className="w-full bg-surface-deck border border-border-subtle rounded-lg py-3 px-space-md text-on-surface font-body-md text-body-md focus:outline-none focus:border-border-focus focus:bg-surface-rack"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <PolicyChecklist policy={policy} met={policyMet} title={t('auth.password.title')} metLabel={t('auth.password.metOf', { met: policyMet, total: policy.length })} />
              <div className="flex flex-col gap-space-xs">
                <label htmlFor="cp" className="field-label">
                  {t('login.confirmPasswordLabel')}
                </label>
                <input
                  id="cp"
                  className="w-full bg-surface-deck border border-border-subtle rounded-lg py-3 px-space-md text-on-surface font-body-md text-body-md focus:outline-none focus:border-border-focus focus:bg-surface-rack"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                  <span className="font-body-sm text-body-sm text-priority-urgent">
                    {t('login.passwordsDontMatch')}
                  </span>
                )}
              </div>
              <button
                className="btn-cyan w-full"
                type="submit"
                disabled={busy || policyMet < policy.length || newPassword !== confirmPassword}
              >
                <Icon name="check_circle" className="text-[20px]" />
                {busy ? t('login.updating') : t('login.saveAndEnter')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
                className="w-full min-h-[44px] py-2.5 px-space-md rounded-lg bg-surface-deck border border-border-subtle text-on-surface-variant font-body-sm text-body-sm hover:text-on-surface transition-colors flex items-center justify-center gap-1"
              >
                <Icon name="close" className="text-[16px]" />
                {t('login.cancelReset')}
              </button>
            </form>
          )}

        </div>
      </main>

      {/* --- Footer fijo con pulso de estado --- */}
      <footer className="fixed bottom-0 inset-x-0 z-40 bg-surface-base/90 backdrop-blur-xl pb-safe border-t border-border-subtle shadow-[0_1px_8px_rgba(0,0,0,0.4)]">
        <div className="h-14 px-margin-mobile flex items-center justify-between text-on-surface-variant font-badge-micro text-badge-micro">
          <div className="flex items-center gap-space-xs">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-goldmine-mint animate-pulse shadow-[0_0_6px_rgba(16,185,129,0.8)]" />
            <span className="uppercase tracking-wider">{t('login.statusOnline')}</span>
          </div>
          <span className="uppercase tracking-wider text-outline">v1.0</span>
        </div>
      </footer>
    </div>
  );
}

/** Checklist visual de políticas de contraseña (AWS Cognito). */
function PolicyChecklist({
  policy,
  met,
  title,
  metLabel,
}: {
  policy: { label: string; ok: boolean }[];
  met: number;
  title: string;
  metLabel: string;
}) {
  return (
    <div className="rounded-xl bg-surface-deck border border-border-subtle p-space-md flex flex-col gap-space-sm shadow-sm">
      <div className="flex items-center justify-between">
        <span className="section-label text-on-surface-variant">{title}</span>
        <span
          className={`font-badge-micro text-badge-micro ${met === policy.length ? 'text-goldmine-mint' : 'text-tertiary'}`}
        >
          {metLabel}
        </span>
      </div>
      <ul className="flex flex-col gap-space-xs">
        {policy.map((rule) => (
          <li key={rule.label} className="flex items-center gap-space-xs font-body-sm text-body-sm">
            <Icon
              name={rule.ok ? 'check_circle' : 'radio_button_unchecked'}
              className={`text-[16px] ${rule.ok ? 'text-goldmine-mint' : 'text-outline'}`}
            />
            <span className={rule.ok ? 'text-goldmine-mint' : 'text-outline'}>{rule.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
